import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Building2, ArrowLeft, Info, ArrowRight, DollarSign, Palette, TrendingUp, FileText, Shield, Zap, Star, Ticket, CalendarCheck, Globe, Bell, Users, ShieldCheck, Sparkles, MapPin, Smartphone, Calendar, Settings, MessageCircle, Copy, LogIn, Check, List, ArrowUpRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import CustomerNavbar from '../components/CustomerNavbar';
import EmailVerificationModal from '../components/EmailVerificationModal';
import ContactAACoreModal from '../components/ContactAACoreModal';
import LoginDialog from '../components/LoginDialog';
import Footer from '../components/Footer';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { getBrowserTimeZone } from '../utils/timezoneUtils';
import { US_STATES } from '../constants/usStates';
import { applyRegistrationIdentityFieldErrors, focusRegistrationField } from '../utils/registrationIdentityErrors';
import { resolveBusinessRegistrationBackTarget } from '../utils/businessRegistrationBack';
import { sendBusinessFunnelEvent } from '../analytics';

interface BusinessInfo {
  businessName: string;
  shortName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  serviceLocation: string;
  phoneNumber: string;
  email: string;
  website?: string;
  description?: string;
  referralCode?: string;
}

interface PersonalInfo {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptAgreement: boolean;
}

type RegistrationStep = 'business' | 'personal';

// Right-side promo sections (AACore Booking + Promotions & Pricing) are only shown until this date
const PROMO_RIGHT_SECTIONS_CUTOFF = new Date('2026-03-31T23:59:59.999Z');

// Phone number formatting utility
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digits
  const phoneNumber = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const trimmed = phoneNumber.slice(0, 10);
  
  // Format as (XXX) XXX-XXXX
  if (trimmed.length === 0) return '';
  if (trimmed.length <= 3) return `(${trimmed}`;
  if (trimmed.length <= 6) return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`;
  return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
};

const BusinessRegistrationPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<RegistrationStep>('business');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);
  const [shortNameAvailability, setShortNameAvailability] = useState<{ available: boolean; checking: boolean; message?: string }>({ available: true, checking: false });
  const [emailAvailability, setEmailAvailability] = useState<{ available: boolean; checking: boolean; message?: string; hasBusinessAccount?: boolean }>({ available: true, checking: false });
  const [referralCodeValidation, setReferralCodeValidation] = useState<{ valid: boolean; checking: boolean; status?: 'EMPTY' | 'VALID' | 'EXPIRED' | 'NOT_FOUND' | 'EXHAUSTED'; message?: string }>({ valid: true, checking: false });
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showContactAACoreModal, setShowContactAACoreModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [isVerificationEmailKnownToExist, setIsVerificationEmailKnownToExist] = useState(false);
  const [isCheckingVerificationEmailExists, setIsCheckingVerificationEmailExists] = useState(false);
  const shortNameCheckSeqRef = useRef(0);
  const emailCheckSeqRef = useRef(0);
  const referralCodeCheckSeqRef = useRef(0);
  const shortNameDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emailDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const referralCodeDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { logout, user, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isAddingBusinessToExistingAccount, setIsAddingBusinessToExistingAccount] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  useEffect(() => {
    sendBusinessFunnelEvent('begin_business_registration');
  }, []);
  const [addBusinessAgreementAccepted, setAddBusinessAgreementAccepted] = useState(false);

  // Get redirect parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect');
  const affiliateRef =
    (searchParams.get('ref') || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12) || undefined;

  const businessForm = useForm<BusinessInfo>({
    defaultValues: {
      serviceLocation: 'IN_STORE'
    }
  });
  const personalForm = useForm<PersonalInfo>();

  const password = personalForm.watch('password');
  const personalEmail = personalForm.watch('email');
  const shortNameValue = businessForm.watch('shortName');

  const handleOpenVerificationModal = () => {
    const normalizedEmail = (personalEmail || '').trim().toLowerCase();
    setVerificationEmail(normalizedEmail && normalizedEmail.includes('@') ? normalizedEmail : '');
    setShowVerificationModal(true);
  };

  const handleCloseVerificationModal = () => {
    setShowVerificationModal(false);
    setVerificationEmail('');
    setIsVerificationEmailKnownToExist(false);
    setIsCheckingVerificationEmailExists(false);
  };

  // When the verification modal is open and the user edits the email there, check if it exists.
  // Only show the "Email verification required..." copy when we know the email exists.
  useEffect(() => {
    if (!showVerificationModal) return;

    const normalizedEmail = verificationEmail.trim().toLowerCase();
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      setIsVerificationEmailKnownToExist(false);
      setIsCheckingVerificationEmailExists(false);
      return;
    }

    setIsCheckingVerificationEmailExists(true);
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const result = await apiService.checkEmailExists(normalizedEmail);
        if (cancelled) return;
        setIsVerificationEmailKnownToExist(!!result.exists);
      } catch {
        if (cancelled) return;
        setIsVerificationEmailKnownToExist(false);
      } finally {
        if (cancelled) return;
        setIsCheckingVerificationEmailExists(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [showVerificationModal, verificationEmail]);

  const handleResendVerification = async () => {
    setIsResendingVerification(true);
    try {
      const normalizedEmail = verificationEmail.trim().toLowerCase();
      if (!normalizedEmail || !normalizedEmail.includes('@')) {
        toast.error('Please enter a valid email address.');
        return;
      }
      const result = await apiService.resendVerification(normalizedEmail);
      if (result.success) {
        toast.success('Verification email sent successfully! Please check your inbox.');
        setShowVerificationModal(false);
        navigate(`/account-verification-sent?email=${encodeURIComponent(normalizedEmail)}&type=business`);
      } else {
        toast.error(result.message || 'Failed to send verification email');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to send verification email';
      toast.error(errorMsg);
    } finally {
      setIsResendingVerification(false);
    }
  };

  // When step 2 (personal) loads, scroll to top so user sees the Account step from the start
  useEffect(() => {
    if (currentStep === 'personal') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  // When step changes to personal: prepopulate Email address with business email (user can change it for login)
  useEffect(() => {
    if (currentStep === 'personal') {
      const prepopulatedEmail = (businessInfo?.email || '').trim().toLowerCase();
      personalForm.reset({
        firstName: '',
        lastName: '',
        email: prepopulatedEmail,
        password: '',
        confirmPassword: '',
        acceptAgreement: false,
      });
      // Force clear any auto-filled values except email (we just prepopulated it from business)
      setTimeout(() => {
        const inputs = document.querySelectorAll('#personalFirstName, #personalLastName, #personalPassword, #personalConfirmPassword');
        inputs.forEach((input: any) => {
          if (input && !input.dataset.userTyped) {
            input.value = '';
          }
        });
      }, 100);
    }
  }, [currentStep, personalForm, businessInfo?.email]);

  // Restore business form data when going back to business step
  useEffect(() => {
    if (currentStep === 'business' && businessInfo) {
      businessForm.reset(businessInfo);
      // If we already progressed past step 1 once, treat the saved short name as valid by default.
      // We'll still do a definitive check on submit (and on short name edits).
      setShortNameAvailability({ available: true, checking: false });
    }
  }, [currentStep, businessInfo, businessForm]);

  const onBusinessSubmit = async (data: BusinessInfo) => {
    setIsLoading(true);
    try {
      // Do a definitive short-name availability check on submit (prevents stale/racey state from blocking step changes).
      const submittedShortName = (data.shortName || '').trim();
      if (submittedShortName.length >= 3) {
        // Cancel any pending debounced check that might fire after submit.
        if (shortNameDebounceTimeoutRef.current) {
          clearTimeout(shortNameDebounceTimeoutRef.current);
          shortNameDebounceTimeoutRef.current = null;
        }

        ++shortNameCheckSeqRef.current;
        setShortNameAvailability((prev) => ({ ...prev, checking: true }));

        const result = await apiService.checkShortNameAvailability(submittedShortName);

        setShortNameAvailability({
          available: result.available,
          checking: false,
          message: result.message,
        });

        if (!result.available) {
          toast.error(result.message || 'The business short name is already taken. Please choose a different one.');
          return;
        }
      }

      // Definitive referral/promo code validation on submit (prevents stale/racey state from allowing step changes).
      const submittedReferralCode = (data.referralCode || '').trim();
      if (submittedReferralCode) {
        if (referralCodeDebounceTimeoutRef.current) {
          clearTimeout(referralCodeDebounceTimeoutRef.current);
          referralCodeDebounceTimeoutRef.current = null;
        }

        ++referralCodeCheckSeqRef.current;
        setReferralCodeValidation((prev) => ({ ...prev, checking: true }));

        const result = await apiService.validatePromoCode(submittedReferralCode);
        setReferralCodeValidation({
          valid: !!result.valid,
          checking: false,
          status: result.status,
          message: result.message,
        });

        if (!result.valid) {
          const msg = result.status === 'EXPIRED' ? 'This code has expired.' : result.status === 'EXHAUSTED' ? 'This code has reached its usage limit.' : 'This code is invalid.';
          businessForm.setError('referralCode', { type: 'manual', message: msg });
          toast.error(msg);
          return;
        }
      }

      setBusinessInfo(data);
      setCurrentStep('personal');
      toast.success('Business information saved!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save business information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onPersonalSubmit = async (data: PersonalInfo) => {
    if (!businessInfo) {
      toast.error('Business information is missing. Please go back and complete business details.');
      return;
    }

    // Add business to existing account (user signed in from "Sign in to add your business")
    if (isAddingBusinessToExistingAccount && user) {
      setIsLoading(true);
      try {
        const detectedTimezone = getBrowserTimeZone();
        const result = await apiService.registerBusinessForExistingUser({
          business: {
            ...businessInfo,
            timezone: detectedTimezone,
            ...(affiliateRef ? { affiliateRef } : {}),
          },
        });
        if (result.user) updateUser(result.user);
        const successUrl = redirectUrl
          ? `/registration-success?type=business&existingAccount=1&redirect=${encodeURIComponent(redirectUrl)}`
          : '/registration-success?type=business&existingAccount=1';
        navigate(successUrl);
      } catch (error: any) {
        const identityFields = applyRegistrationIdentityFieldErrors(error, (field, message) => {
          if (field === 'phone') {
            businessForm.setError('phoneNumber', { type: 'server', message });
          }
          if (field === 'businessEmail' || field === 'email') {
            businessForm.setError('email', { type: 'server', message });
          }
        });
        if (identityFields.includes('phone')) {
          setCurrentStep('business');
          focusRegistrationField('businessPhoneNumber');
          return;
        }
        if (identityFields.includes('businessEmail') || identityFields.includes('email')) {
          setCurrentStep('business');
          focusRegistrationField('businessEmail');
          return;
        }
        toast.error(error.response?.data?.message || 'Failed to add business. Please try again.');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    try {
      // Do a definitive email availability check on submit (prevents stale/racey state from blocking submission).
      const submittedEmail = (data.email || '').trim().toLowerCase();
      if (submittedEmail.includes('@')) {
        if (emailDebounceTimeoutRef.current) {
          clearTimeout(emailDebounceTimeoutRef.current);
          emailDebounceTimeoutRef.current = null;
        }

        ++emailCheckSeqRef.current;
        setEmailAvailability((prev) => ({ ...prev, checking: true }));
        const result = await apiService.checkEmailExists(submittedEmail);

        setEmailAvailability({
          available: !result.exists,
          checking: false,
          message: result.exists
            ? (result.hasBusinessAccount ? 'This email is already registered with a business account' : 'Sign in to add your business')
            : 'Email is available',
          hasBusinessAccount: result.exists ? result.hasBusinessAccount : undefined,
        });

        if (result.exists) {
          if (result.hasBusinessAccount) {
            toast.error('This email is already registered with a business account. Sign in to manage your business or use a different email.');
            return;
          }
          // Customer account exists without a business: show login dialog to add business
          setShowLoginDialog(true);
          return;
        }
      }

      // Detect browser timezone
      const detectedTimezone = getBrowserTimeZone();

      // Create business and user records
      const registrationData = {
        business: {
          ...businessInfo,
          timezone: detectedTimezone,
          ...(affiliateRef ? { affiliateRef } : {}),
        },
        user: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          password: data.password,
        }
      };

      await apiService.registerBusiness(registrationData);
      
      // Redirect to success page
      setTimeout(() => {
        // Preserve redirect parameter if it exists and include type=business
        const successUrl = redirectUrl 
          ? `/registration-success?type=business&redirect=${encodeURIComponent(redirectUrl)}` 
          : '/registration-success?type=business';
        navigate(successUrl);
      }, 1000);
    } catch (error: any) {
      const identityFields = applyRegistrationIdentityFieldErrors(error, (field, message) => {
        if (field === 'phone') {
          businessForm.setError('phoneNumber', { type: 'server', message });
          return;
        }
        if (field === 'businessEmail') {
          businessForm.setError('email', { type: 'server', message });
          return;
        }
        personalForm.setError(field, { type: 'server', message });
      });
      if (identityFields.length > 0) {
        if (identityFields.includes('phone')) {
          setCurrentStep('business');
          focusRegistrationField('businessPhoneNumber');
        } else if (identityFields.includes('businessEmail')) {
          setCurrentStep('business');
          focusRegistrationField('businessEmail');
        } else if (identityFields.includes('email')) {
          focusRegistrationField('personalEmail');
        } else {
          focusRegistrationField(identityFields[0] === 'lastName' ? 'personalLastName' : 'personalFirstName');
        }
        return;
      }
      toast.error(error.response?.data?.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToBusiness = () => {
    setAddBusinessAgreementAccepted(false);
    setCurrentStep('business');
  };

  const handleBack = () => {
    const historyState = window.history.state as { idx?: number } | null;
    const target = resolveBusinessRegistrationBackTarget({
      historyIndex: historyState?.idx,
      historyLength: window.history.length,
      referrer: document.referrer,
      currentUrl: window.location.href,
    });
    if (target === -1) {
      navigate(-1);
      return;
    }
    navigate(target);
  };

  const handleAddBusinessToAccount = async () => {
    if (!addBusinessAgreementAccepted) {
      toast.error('You must agree to the Business Account Agreement to continue.');
      return;
    }
    const formValues = businessForm.getValues();
    const source = (formValues?.businessName != null && formValues?.shortName != null)
      ? { ...formValues }
      : businessInfo;
    if (!source || !user) {
      if (!source) toast.error('Business information is missing. Please go back and complete business details.');
      return;
    }
    setIsLoading(true);
    try {
      const detectedTimezone = getBrowserTimeZone();
      const businessPayload = {
        businessName: source.businessName ?? '',
        shortName: source.shortName ?? '',
        address: source.address ?? '',
        city: source.city ?? '',
        state: source.state ?? '',
        zipCode: source.zipCode ?? '',
        serviceLocation: source.serviceLocation ?? 'IN_STORE',
        phoneNumber: source.phoneNumber ?? '',
        email: source.email ?? '',
        website: source.website ?? '',
        description: source.description ?? '',
        referralCode: source.referralCode ?? undefined,
        timezone: detectedTimezone,
        ...(affiliateRef ? { affiliateRef } : {}),
      };
      const result = await apiService.registerBusinessForExistingUser({
        business: businessPayload,
      });
      if (result.user) updateUser(result.user);
      const successUrl = redirectUrl
        ? `/registration-success?type=business&existingAccount=1&redirect=${encodeURIComponent(redirectUrl)}`
        : '/registration-success?type=business&existingAccount=1';
      navigate(successUrl);
    } catch (error: any) {
      const identityFields = applyRegistrationIdentityFieldErrors(error, (field, message) => {
        if (field === 'phone') {
          businessForm.setError('phoneNumber', { type: 'server', message });
        }
        if (field === 'businessEmail' || field === 'email') {
          businessForm.setError('email', { type: 'server', message });
        }
      });
      if (identityFields.includes('phone')) {
        setCurrentStep('business');
        focusRegistrationField('businessPhoneNumber');
        return;
      }
      if (identityFields.includes('businessEmail') || identityFields.includes('email')) {
        setCurrentStep('business');
        focusRegistrationField('businessEmail');
        return;
      }
      toast.error(error.response?.data?.message || 'Failed to add business. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Function to disable autocomplete on input fields
  const disableAutocomplete = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = event.target;
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('data-autocomplete', 'off');
    input.setAttribute('data-lpignore', 'true');
    input.setAttribute('data-form-type', 'other');
    
    // Clear the input value to prevent auto-population (only if not user-typed)
    if (input.value && !input.dataset.userTyped) {
      input.value = '';
    }
  };

  // Function to handle user input and mark as user-typed
  const handleUserInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    event.target.dataset.userTyped = 'true';
  };

  // Function to check short name availability
  const checkShortNameAvailability = useCallback(async (shortName: string) => {
    if (!shortName || shortName.length < 3) {
      setShortNameAvailability({ available: true, checking: false });
      return;
    }

    if (!/^[a-z0-9_-]+$/.test(shortName)) {
      setShortNameAvailability({ available: true, checking: false });
      return;
    }

    const requestSeq = ++shortNameCheckSeqRef.current;
    // Keep `available: true` while checking to avoid flashing an "unavailable" error state during lookup.
    setShortNameAvailability((prev) => ({ ...prev, available: true, checking: true }));
    
    try {
      const result = await apiService.checkShortNameAvailability(shortName);

      // Ignore stale results and ignore results for a shortName that is no longer the current field value.
      if (requestSeq !== shortNameCheckSeqRef.current) return;
      if (businessForm.getValues('shortName') !== shortName) return;

      setShortNameAvailability({
        available: result.available,
        checking: false,
        message: result.message,
      });
    } catch (error) {
      console.error('Error checking short name availability:', error);

      if (requestSeq !== shortNameCheckSeqRef.current) return;
      if (businessForm.getValues('shortName') !== shortName) return;

      setShortNameAvailability({
        available: true,
        checking: false,
        message: 'Unable to verify availability. Please try again.',
      });
    }
  }, [businessForm]);

  const checkReferralCodeValidity = useCallback(async (code: string) => {
    if (!code || code.trim().length === 0) {
      setReferralCodeValidation({ valid: true, checking: false, status: 'EMPTY' });
      businessForm.clearErrors('referralCode');
      return;
    }

    const normalized = code.trim();
    const requestSeq = ++referralCodeCheckSeqRef.current;
    // Keep valid=true while checking to avoid flashing an error state during lookup.
    setReferralCodeValidation((prev) => ({ ...prev, valid: true, checking: true }));

    try {
      const result = await apiService.validatePromoCode(normalized);

      if (requestSeq !== referralCodeCheckSeqRef.current) return;
      if ((businessForm.getValues('referralCode') || '').trim() !== normalized) return;

      setReferralCodeValidation({
        valid: !!result.valid,
        checking: false,
        status: result.status,
        message: result.message,
      });

      if (!result.valid) {
        const msg = result.status === 'EXPIRED' ? 'This code has expired.' : result.status === 'EXHAUSTED' ? 'This code has reached its usage limit.' : 'This code is invalid.';
        businessForm.setError('referralCode', { type: 'manual', message: msg });
      } else {
        businessForm.clearErrors('referralCode');
      }
    } catch (error) {
      console.error('Error validating referral code:', error);

      if (requestSeq !== referralCodeCheckSeqRef.current) return;
      if ((businessForm.getValues('referralCode') || '').trim() !== normalized) return;

      setReferralCodeValidation({
        valid: true,
        checking: false,
        status: 'EMPTY',
        message: 'Unable to validate code right now.',
      });
      // Do not block the user when validation fails due to network/server issues.
      businessForm.clearErrors('referralCode');
    }
  }, [businessForm]);

  // Debounced short name check
  const debouncedShortNameCheck = useCallback((shortName: string) => {
    if (shortNameDebounceTimeoutRef.current) {
      clearTimeout(shortNameDebounceTimeoutRef.current);
    }
    shortNameDebounceTimeoutRef.current = setTimeout(() => {
      void checkShortNameAvailability(shortName);
    }, 500);
  }, [checkShortNameAvailability]);

  const debouncedReferralCodeCheck = useCallback((code: string) => {
    if (referralCodeDebounceTimeoutRef.current) {
      clearTimeout(referralCodeDebounceTimeoutRef.current);
    }
    referralCodeDebounceTimeoutRef.current = setTimeout(() => {
      void checkReferralCodeValidity(code);
    }, 400);
  }, [checkReferralCodeValidity]);

  // Function to check email availability
  const checkEmailAvailability = useCallback(async (email: string) => {
    if (!email || !email.includes('@')) {
      setEmailAvailability({ available: true, checking: false });
      return;
    }

    const requestSeq = ++emailCheckSeqRef.current;
    // Keep `available: true` while checking to avoid flashing an error state during lookup.
    setEmailAvailability((prev) => ({ ...prev, available: true, checking: true }));
    
    try {
      const result = await apiService.checkEmailExists(email);

      if (requestSeq !== emailCheckSeqRef.current) return;
      if (personalForm.getValues('email') !== email) return;

      setEmailAvailability({
        available: !result.exists,
        checking: false,
        message: result.exists
          ? (result.hasBusinessAccount ? 'This email is already registered with a business account' : 'Sign in to add your business')
          : 'Email is available',
        hasBusinessAccount: result.exists ? result.hasBusinessAccount : undefined,
      });
    } catch (error) {
      console.error('Error checking email availability:', error);

      if (requestSeq !== emailCheckSeqRef.current) return;
      if (personalForm.getValues('email') !== email) return;

      setEmailAvailability({
        available: true,
        checking: false,
        message: 'Unable to verify email availability. Please try again.',
      });
    }
  }, [personalForm]);

  // Debounced email check
  const debouncedEmailCheck = useCallback((email: string) => {
    if (emailDebounceTimeoutRef.current) {
      clearTimeout(emailDebounceTimeoutRef.current);
    }
    emailDebounceTimeoutRef.current = setTimeout(() => {
      void checkEmailAvailability(email);
    }, 500);
  }, [checkEmailAvailability]);

  // Cleanup any pending debounced checks
  useEffect(() => {
    return () => {
      if (shortNameDebounceTimeoutRef.current) clearTimeout(shortNameDebounceTimeoutRef.current);
      if (emailDebounceTimeoutRef.current) clearTimeout(emailDebounceTimeoutRef.current);
      if (referralCodeDebounceTimeoutRef.current) clearTimeout(referralCodeDebounceTimeoutRef.current);
    };
  }, []);

  return (
    <div className="min-h-screen no-animations flex flex-col relative">
      {/* Customer Navbar */}
      <CustomerNavbar 
        onLogout={handleLogout}
        showDashboardLink={false}
        showLogoutButton={false}
        showAuthButtons={true}
      />

      <LoginDialog
        isOpen={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        prefillEmail={personalForm.watch('email')}
        onSuccess={() => {
          setShowLoginDialog(false);
          setIsAddingBusinessToExistingAccount(true);
        }}
      />
      
      {/* Main Content */}
      <div className="flex-1 min-w-0 pt-6 pb-12">
        <div className="px-4 lg:px-8 max-w-7xl mx-auto">
          {/* Breadcrumb Navigation */}
          <div className="mb-4">
            {currentStep === 'personal' ? (
              <button
                onClick={handleBackToBusiness}
                className="flex items-center font-semibold"
                style={{ color: '#0B4778' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to business information
              </button>
            ) : (
              <button
                onClick={handleBack}
                className="flex items-center font-semibold"
                style={{ color: '#0B4778' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#0a3d66'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#0B4778'}
              >
                &lt;- Back
              </button>
            )}
          </div>

          {/* Header */}
          <div className="mb-4">
            <h1 className="text-xl font-bold mb-0">
              {currentStep === 'business' ? 'Business Information' : 'Account Information'}
            </h1>
            <p className="text-gray-600 text-sm mb-0">
              {currentStep === 'business' 
                ? 'Tell us about your business'
                : 'Create your account to manage your business'
              }
            </p>
          </div>

          <div className="space-y-8">
            {/* Form Card */}
            <div className="md:flex md:gap-6 md:items-stretch">
              <div className="md:w-1/2 md:flex md:flex-col">
                <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm flex flex-col flex-1">
                {/* Progress Steps */}
                <div className="mb-6 flex justify-center">
                  <div className="flex items-center space-x-4">
                    <div
                      className={`flex items-center ${currentStep === 'business' ? '' : 'text-green-400'} ${currentStep === 'personal' ? 'cursor-pointer hover:text-blue-600' : ''}`}
                      style={currentStep === 'business' ? { color: '#0B4778' } : {}}
                      onClick={currentStep === 'personal' ? () => setCurrentStep('business') : undefined}
                      role={currentStep === 'personal' ? 'button' as const : undefined}
                      tabIndex={currentStep === 'personal' ? 0 : -1}
                      onKeyDown={currentStep === 'personal' ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setCurrentStep('business');
                        }
                      } : undefined}
                      title={currentStep === 'personal' ? 'Back to business information' : undefined}
                      aria-disabled={currentStep !== 'personal'}
                    >
                      <div 
                        className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                          currentStep === 'business' ? '' : 'bg-green-500 border-green-500'
                        }`}
                        style={currentStep === 'business' ? { backgroundColor: '#0B4778', borderColor: '#0B4778' } : {}}
                      >
                        <span className="text-white text-sm font-bold">1</span>
                      </div>
                      <span className={`ml-2 text-sm font-medium underline-offset-2 ${currentStep === 'personal' ? 'hover:underline' : ''}`}>Business</span>
                    </div>
                    <div className="w-8 h-0.5 bg-gray-400"></div>
                    <div 
                      className={`flex items-center ${currentStep === 'personal' ? '' : 'text-gray-400'}`}
                      style={currentStep === 'personal' ? { color: '#0B4778' } : {}}
                    >
                      <div 
                        className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                          currentStep === 'personal' ? '' : 'bg-gray-300 border-gray-300'
                        }`}
                        style={currentStep === 'personal' ? { backgroundColor: '#0B4778', borderColor: '#0B4778' } : {}}
                      >
                        <span className="text-white text-sm font-bold">2</span>
                      </div>
                      <span className="ml-2 text-sm font-medium">Account</span>
                    </div>
                  </div>
                </div>

              {currentStep === 'business' ? (
                // Business Information Form
                <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={businessForm.handleSubmit(onBusinessSubmit)} autoComplete="off" noValidate>
              {/* Hidden fields to trick browser autocomplete */}
              <input type="text" style={{ display: 'none' }} autoComplete="off" />
              {/* Use type="text" for hidden autofill traps to avoid "invalid form control ... not focusable" */}
              <input type="text" style={{ display: 'none' }} autoComplete="off" />
              <input type="text" style={{ display: 'none' }} autoComplete="off" />
              <input type="text" style={{ display: 'none' }} autoComplete="off" />
              <input type="text" name="fake-business-name" style={{ display: 'none' }} autoComplete="off" />
              <input type="text" name="fake-address" style={{ display: 'none' }} autoComplete="off" />
              <input type="text" name="fake-city" style={{ display: 'none' }} autoComplete="off" />
              <input type="text" name="fake-state" style={{ display: 'none' }} autoComplete="off" />
              <input type="text" name="fake-zip" style={{ display: 'none' }} autoComplete="off" />
              <input type="tel" name="fake-phone" style={{ display: 'none' }} autoComplete="off" />
              <input type="url" name="fake-website" style={{ display: 'none' }} autoComplete="off" />
              {/* Business Name */}
              <div className="md:col-span-2">
                <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name *
                </label>
                <div className="relative">
                  <input
                    id="businessName"
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`aacore-textfield-primary ${businessForm.formState.errors.businessName ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Enter your business name"
                    {...businessForm.register('businessName', {
                      required: 'Business name is required',
                      minLength: {
                        value: 2,
                        message: 'Business name must be at least 2 characters',
                      },
                    })}
                  />
                </div>
                {businessForm.formState.errors.businessName && (
                  <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.businessName.message}</p>
                )}
              </div>

              {/* Short Name */}
              <div className="md:col-span-2">
                <label htmlFor="shortName" className="block text-sm font-medium text-gray-700 mb-2">
                  Business Short Name *
                </label>
                <div className="flex flex-col gap-2 lg:flex-row lg:gap-0">
                  <div className="flex items-center px-3 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm font-medium lg:rounded-l-lg lg:rounded-r-none lg:border-r-0">
                    https://booking.aacore.net/<span className="lg:hidden">{shortNameValue?.trim() ? <span className="font-bold">{shortNameValue.trim()}</span> : <>{'{'}<span className="font-bold">yourstore</span>{'}'}</>}</span>
                  </div>
                  <input
                    id="businessShortName"
                    type="text"
                    maxLength={25}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`w-full lg:flex-1 aacore-textfield-primary rounded-lg lg:rounded-l-none lg:rounded-r-md ${
                      businessForm.formState.errors.shortName || (!shortNameAvailability.available && !shortNameAvailability.checking) 
                        ? 'border-red-500 focus:ring-red-500' 
                        : ''
                    }`}
                    placeholder="yourstore"
                    {...businessForm.register('shortName', {
                      required: 'Short name is required',
                      pattern: {
                        value: /^[a-z0-9_-]+$/,
                        message: 'Short name can only contain lowercase letters, numbers, hyphens, and underscores',
                      },
                      minLength: {
                        value: 3,
                        message: 'Short name must be at least 3 characters',
                      },
                      maxLength: {
                        value: 25,
                        message: 'Short name must be less than 25 characters',
                      },
                      onChange: (e) => {
                        const value = e.target.value.toLowerCase().slice(0, 25);
                        e.target.value = value;
                        businessForm.setValue('shortName', value, { shouldValidate: true });
                        debouncedShortNameCheck(value);
                      },
                    })}
                  />
                </div>
                {businessForm.formState.errors.shortName && (
                  <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.shortName.message}</p>
                )}
                {shortNameAvailability.checking && (
                  <p className="mt-1 text-sm text-blue-900 flex items-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-900 mr-2"></div>
                    Checking availability...
                  </p>
                )}
                {!shortNameAvailability.checking && !shortNameAvailability.available && (
                  <p className="mt-1 text-sm text-red-600">
                    {shortNameAvailability.message || 'This short name is already taken. Please choose a different one.'}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  This will be your business URL
                </p>
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address *
                </label>
                <div className="relative">
                  <input
                    id="businessAddress"
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`aacore-textfield-primary ${businessForm.formState.errors.address ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Enter street address"
                    {...businessForm.register('address', {
                      required: 'Address is required',
                    })}
                  />
                </div>
                {businessForm.formState.errors.address && (
                  <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.address.message}</p>
                )}
              </div>

              {/* City, State, Zip */}
              <div className="md:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      id="businessCity"
                      type="text"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      className={`aacore-textfield-primary ${businessForm.formState.errors.city ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="City"
                      {...businessForm.register('city', {
                        required: 'City is required',
                      })}
                    />
                    {businessForm.formState.errors.city && (
                      <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.city.message}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                      State *
                    </label>
                    <select
                      id="businessState"
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      className={`aacore-textfield-primary ${businessForm.formState.errors.state ? 'border-red-500 focus:ring-red-500' : ''}`}
                      {...businessForm.register('state', {
                        required: 'State is required',
                      })}
                    >
                      <option value="">Select a state</option>
                      {US_STATES.map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                    {businessForm.formState.errors.state && (
                      <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.state.message}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                      ZIP Code *
                    </label>
                    <input
                      id="businessZipCode"
                      type="text"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-lpignore="true"
                      data-form-type="other"
                      className={`aacore-textfield-primary ${businessForm.formState.errors.zipCode ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="ZIP Code"
                      {...businessForm.register('zipCode', {
                        required: 'ZIP Code is required',
                        pattern: {
                          value: /^\d{5}(-\d{4})?$/,
                          message: 'Please enter a valid ZIP Code',
                        },
                      })}
                    />
                    {businessForm.formState.errors.zipCode && (
                      <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.zipCode.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Service Location */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Location *
                </label>
                <p className="text-sm text-gray-600 mb-4">Where will services be performed? Default is In-store.</p>
                <div className="flex flex-col gap-4 md:flex-row md:gap-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      value="IN_STORE"
                      {...businessForm.register('serviceLocation', {
                        required: 'Service location is required'
                      })}
                      className="mt-1 w-4 h-4 border-gray-300 focus:ring-[#0B4778] text-[#0B4778] service-location-radio"
                    />
                    <div>
                      <div className="font-semibold text-sm text-gray-900">In-store</div>
                      <div className="text-xs text-gray-500">Business' site</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      value="ON_SITE"
                      {...businessForm.register('serviceLocation', {
                        required: 'Service location is required'
                      })}
                      className="mt-1 w-4 h-4 border-gray-300 focus:ring-[#0B4778] text-[#0B4778] service-location-radio"
                    />
                    <div>
                      <div className="font-semibold text-sm text-gray-900">On-site</div>
                      <div className="text-xs text-gray-500">Customer's site</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      value="BOTH"
                      {...businessForm.register('serviceLocation', {
                        required: 'Service location is required'
                      })}
                      className="mt-1 w-4 h-4 border-gray-300 focus:ring-[#0B4778] text-[#0B4778] service-location-radio"
                    />
                    <div>
                      <div className="font-semibold text-sm text-gray-900">Both</div>
                      <div className="text-xs text-gray-500">In-store & On-site</div>
                    </div>
                  </label>
                </div>
                {businessForm.formState.errors.serviceLocation && (
                  <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.serviceLocation.message}</p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <input
                    id="businessPhoneNumber"
                    type="tel"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`aacore-textfield-primary ${businessForm.formState.errors.phoneNumber ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="(555) 123-4567"
                    {...businessForm.register('phoneNumber', {
                      required: 'Phone number is required',
                      pattern: {
                        value: /^\(\d{3}\) \d{3}-\d{4}$/,
                        message: 'Please enter a valid phone number in format (123) 456-7890',
                      },
                      onChange: (e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        e.target.value = formatted;
                        // Update the form value without triggering re-render
                        businessForm.setValue('phoneNumber', formatted, { shouldValidate: false });
                      },
                    })}
                  />
                </div>
                {businessForm.formState.errors.phoneNumber && (
                  <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.phoneNumber.message}</p>
                )}
              </div>

              {/* Business Email */}
              <div>
                <label htmlFor="businessEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Business Email *
                </label>
                <div className="relative">
                  <input
                    id="businessEmail"
                    type="email"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`aacore-textfield-primary ${businessForm.formState.errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="contact@yourbusiness.com"
                    {...businessForm.register('email', {
                      required: 'Business email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address',
                      },
                      onChange: (e) => {
                        const value = e.target.value.trim();
                        businessForm.setValue('email', value, { shouldValidate: false });
                      },
                    })}
                  />
                </div>
                {businessForm.formState.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.email.message}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">This email will receive notifications and customer messages.</p>
              </div>

              {/* Website (Optional) - full width on desktop */}
              <div className="md:col-span-2">
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                  Website (Optional)
                </label>
                <div className="relative">
                  <input
                    id="businessWebsite"
                    type="url"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`aacore-textfield-primary ${businessForm.formState.errors.website ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="https://www.yourwebsite.com"
                    {...businessForm.register('website', {
                      pattern: {
                        value: /^https?:\/\/.+/,
                        message: 'Please enter a valid URL starting with http:// or https://',
                      },
                    })}
                  />
                </div>
                {businessForm.formState.errors.website && (
                  <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.website.message}</p>
                )}
              </div>

              {/* Description (Optional) */}
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Business Description (Optional)
                </label>
                <textarea
                  id="businessDescription"
                  rows={3}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-lpignore="true"
                  data-form-type="other"
                  className={`aacore-textfield-primary resize-none ${businessForm.formState.errors.description ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Brief description of your business..."
                  {...businessForm.register('description', {
                    maxLength: {
                      value: 500,
                      message: 'Description must be less than 500 characters',
                    },
                  })}
                />
                {businessForm.formState.errors.description && (
                  <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.description.message}</p>
                )}
              </div>

              {/* Referral Code (Optional) */}
              <div className="md:col-span-2">
                <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-2">
                  Referral Code <span className="font-normal text-gray-500">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute left-0 top-0 bottom-0 flex items-center pl-3 pointer-events-none">
                    <Ticket className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="businessReferralCode"
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`aacore-textfield-primary pl-10 ${businessForm.formState.errors.referralCode ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Enter code"
                    {...businessForm.register('referralCode', {
                      maxLength: {
                        value: 100,
                        message: 'Referral code must be less than 100 characters',
                      },
                      onChange: (e) => {
                        const value = (e.target.value || '').toString();
                        // Keep the form field in sync without aggressive normalization (codes can be mixed case)
                        businessForm.setValue('referralCode', value, { shouldValidate: false });
                        debouncedReferralCodeCheck(value);
                      },
                    })}
                  />
                </div>
                {businessForm.formState.errors.referralCode && (
                  <p className="mt-1 text-sm text-red-600">{businessForm.formState.errors.referralCode.message}</p>
                )}
                {!businessForm.formState.errors.referralCode && referralCodeValidation.checking && (
                  <p className="mt-1 text-sm text-blue-900 flex items-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-900 mr-2"></div>
                    Checking code...
                  </p>
                )}
                {!businessForm.formState.errors.referralCode && !referralCodeValidation.checking && referralCodeValidation.status === 'VALID' && (
                  <p className="mt-1 text-sm text-green-700">Code applied.</p>
                )}
                {!businessForm.formState.errors.referralCode && !referralCodeValidation.checking && referralCodeValidation.status === 'EXPIRED' && (
                  <p className="mt-1 text-sm text-red-600">This code has expired.</p>
                )}
                {!businessForm.formState.errors.referralCode && !referralCodeValidation.checking && referralCodeValidation.status === 'EXHAUSTED' && (
                  <p className="mt-1 text-sm text-red-600">This code has reached its usage limit.</p>
                )}
                {!businessForm.formState.errors.referralCode && !referralCodeValidation.checking && referralCodeValidation.status === 'NOT_FOUND' && (
                  <p className="mt-1 text-sm text-red-600">This code is invalid.</p>
                )}
                <p className="mt-1 text-sm text-gray-600">If you received a referral code, enter it here.</p>
              </div>

              {/* Next Button */}
              <div className="md:col-span-2 flex justify-center">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full md:w-auto justify-center aacore-btn-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 mr-2" />
                      Next: Create Account
                    </div>
                  )}
                </button>
              </div>
            </form>
          ) : user ? (
                // Logged-in user on step 2: only agreement + Complete Registration (no name/email/password)
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2 rounded-lg bg-blue-50 border border-blue-200 p-4">
                    <p className="text-sm text-blue-900">
                      You&apos;re signed in as <strong>{user.email}</strong>. We&apos;ll add this business to your account. Agree to the terms below and click <strong>Complete Registration</strong> to finish.
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addBusinessAgreementAccepted}
                        onChange={(e) => setAddBusinessAgreementAccepted(e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
                      />
                      <span className="text-sm text-gray-700">
                        I agree to the{' '}
                        <a href="/agreements/business" target="_blank" rel="noopener noreferrer" className="text-blue-900 hover:underline">
                          AACore Business Account Agreement
                        </a>
                        <span className="text-red-500"> *</span>
                      </span>
                    </label>
                  </div>
                  <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      type="button"
                      onClick={handleBackToBusiness}
                      className="aacore-btn-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                    >
                      <ArrowLeft className="w-4 h-4" aria-hidden />
                      Back to business details
                    </button>
                    <button
                      type="button"
                      onClick={handleAddBusinessToAccount}
                      disabled={isLoading || !addBusinessAgreementAccepted}
                      className="aacore-btn-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900 inline-flex items-center justify-center gap-2 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Completing registration...
                        </div>
                      ) : (
                        <>
                          <Building2 className="w-4 h-4 mr-2 inline" />
                          Complete Registration
                        </>
                      )}
                    </button>
                  </div>
                </div>
          ) : (
                // Personal Information Form
                <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={personalForm.handleSubmit(onPersonalSubmit)} autoComplete="off" noValidate>
              {/* Hidden fields to trick browser autocomplete */}
              <input type="text" style={{ display: 'none' }} autoComplete="off" />
              {/* Use type="text" for hidden autofill traps to avoid native constraint validation on hidden controls */}
              <input type="text" style={{ display: 'none' }} autoComplete="off" />
              <input type="text" style={{ display: 'none' }} autoComplete="off" />
              <input type="text" name="fake-first-name" style={{ display: 'none' }} autoComplete="off" />
              <input type="text" name="fake-last-name" style={{ display: 'none' }} autoComplete="off" />
              <input type="email" name="fake-email" style={{ display: 'none' }} autoComplete="off" />
              
              {/* First Name / Last Name - hidden when email is existing customer (sign in to add business) */}
              {!(emailAvailability.available === false && emailAvailability.hasBusinessAccount === false) && (
              <>
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <div className="relative">
                  <input
                    id="personalFirstName"
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`aacore-textfield-primary ${personalForm.formState.errors.firstName ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Enter your first name"
                    {...personalForm.register('firstName', {
                      required: 'First name is required',
                      minLength: {
                        value: 2,
                        message: 'First name must be at least 2 characters',
                      },
                    })}
                  />
                </div>
                {personalForm.formState.errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{personalForm.formState.errors.firstName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <div className="relative">
                  <input
                    id="personalLastName"
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`aacore-textfield-primary ${personalForm.formState.errors.lastName ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Enter your last name"
                    {...personalForm.register('lastName', {
                      required: 'Last name is required',
                      minLength: {
                        value: 2,
                        message: 'Last name must be at least 2 characters',
                      },
                    })}
                  />
                </div>
                {personalForm.formState.errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{personalForm.formState.errors.lastName.message}</p>
                )}
              </div>
              </>
              )}

              {/* Email */}
              <div className="md:col-span-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <input
                    id="personalEmail"
                    type="email"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`aacore-textfield-primary ${
                      personalForm.formState.errors.email || (!emailAvailability.available && !emailAvailability.checking) 
                        ? 'border-red-500 focus:ring-red-500' 
                        : ''
                    }`}
                    placeholder="Enter your email address"
                    {...personalForm.register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                      onChange: (e) => {
                        const value = e.target.value.toLowerCase();
                        e.target.value = value;
                        personalForm.setValue('email', value, { shouldValidate: false });
                        debouncedEmailCheck(value);
                      },
                    })}
                  />
                </div>
                {personalForm.formState.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{personalForm.formState.errors.email.message}</p>
                )}
                {emailAvailability.checking && (
                  <p className="mt-1 text-sm text-blue-900 flex items-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-900 mr-2"></div>
                    Checking email availability...
                  </p>
                )}
                {!emailAvailability.checking && !emailAvailability.available && (
                  <div className="mt-1">
                    <p className="text-sm text-red-600">
                      {emailAvailability.hasBusinessAccount
                        ? 'This email is already registered with a business account. Sign in to manage your business or use a different email.'
                        : emailAvailability.message || 'This email is already registered. Please use a different email.'}
                    </p>
                    {emailAvailability.hasBusinessAccount === true && (
                      <Link to="/login" className="mt-2 inline-block text-sm font-medium text-blue-600 hover:text-blue-800 underline">
                        Sign in
                      </Link>
                    )}
                  </div>
                )}
              </div>

              {/* Password - hidden when email is existing customer (sign in to add business) */}
              {!(emailAvailability.available === false && emailAvailability.hasBusinessAccount === false) && (
              <>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    id="personalPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`aacore-textfield-primary pr-10 ${personalForm.formState.errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Create a password"
                    {...personalForm.register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                        message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
                      },
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {personalForm.formState.errors.password && (
                  <p className="mt-1 text-sm text-red-600">{personalForm.formState.errors.password.message}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    id="personalConfirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="other"
                    className={`aacore-textfield-primary pr-10 ${personalForm.formState.errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Confirm your password"
                    {...personalForm.register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) => value === password || 'Passwords do not match',
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {personalForm.formState.errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{personalForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>
              </>
              )}

              {/* Business Agreement */}
              <div className="md:col-span-2">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 w-4 h-4 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
                    {...personalForm.register('acceptAgreement', {
                      validate: (v) => v === true || 'You must agree to the Business Account Agreement',
                    })}
                  />
                  <span className="text-sm text-gray-700">
                    I agree to the{' '}
                    <a href="/agreements/business" target="_blank" rel="noopener noreferrer" className="text-blue-900 hover:underline">
                      AACore Business Account Agreement
                    </a>
                    <span className="text-red-500"> *</span>
                  </span>
                </label>
                {personalForm.formState.errors.acceptAgreement && (
                  <p className="mt-1 text-sm text-red-600">{personalForm.formState.errors.acceptAgreement.message}</p>
                )}
              </div>

              {/* Create Account / Login Button */}
              <div className="md:col-span-2 flex justify-center">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full md:w-auto justify-center aacore-btn-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {emailAvailability.available === false && emailAvailability.hasBusinessAccount === false
                        ? 'Opening...'
                        : 'Creating account...'}
                    </div>
                  ) : emailAvailability.available === false && emailAvailability.hasBusinessAccount === false ? (
                    <div className="flex items-center">
                      <LogIn className="w-4 h-4 mr-2" />
                      Login to your account to continue
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 mr-2" />
                      Create Business Account
                    </div>
                  )}
                </button>
              </div>
            </form>
          )}

              </div>
            </div>

              {/* Helpful Links and Info Cards (right side on desktop) */}
              <div className="md:w-1/2 mt-4 md:mt-0 md:flex md:flex-col space-y-6">
                {/* Helpful links section */}
                <div className="bg-white border border-gray-200 p-5 rounded-lg shadow-sm">
                  <div className="flex gap-3 items-start">
                    <div className="hidden md:flex flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <Info className="h-5 w-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <strong className="text-gray-900 block mb-3">Helpful links</strong>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <ArrowRight className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span>
                            Already have an account?{' '}
                            <Link to="/login?type=business" className="font-medium text-blue-900 hover:text-blue-800">
                              Sign in here
                            </Link>
                            .
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <ArrowRight className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span>
                            Have an account but not yet verified?{' '}
                            <button
                              type="button"
                              onClick={handleOpenVerificationModal}
                              className="font-medium text-blue-900 hover:text-blue-800"
                            >
                              Complete verification here
                            </button>
                            .
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <ArrowRight className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span>
                            Forgot your password?{' '}
                            <Link to="/forgot-password?type=business" className="font-medium text-blue-900 hover:text-blue-800">
                              Reset it
                            </Link>
                            .
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* AACore Booking + Promotions & Pricing (only until March 31, 2026) */}
                {new Date() <= PROMO_RIGHT_SECTIONS_CUTOFF && (
                  <>
                    {/* Section 1: AACore Booking Features and Perks */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0B4778' }}>
                          <CalendarCheck className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">AACore Booking</h3>
                          <p className="text-sm text-gray-600">Online booking + simple business tools for small teams.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        <div className="bg-gray-100 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <Globe className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-sm text-gray-900">Free Website</p>
                              <p className="text-xs text-gray-600">Every account includes a clean, no-branding website with a friendly URL.</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-100 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <Bell className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-sm text-gray-900">Notifications</p>
                              <p className="text-xs text-gray-600">Email/text confirmations, reminders, and updates (configurable).</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-100 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <Users className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-sm text-gray-900">Multi-user</p>
                              <p className="text-xs text-gray-600">Add staff accounts and manage access by role.</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-100 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <ShieldCheck className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-sm text-gray-900">Secure by design</p>
                              <p className="text-xs text-gray-600">Built with modern security practices and a focus on reliability.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          <Sparkles className="w-3.5 h-3.5" />
                          Early adopter perks
                        </span>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          <MapPin className="w-3.5 h-3.5" />
                          Built in Virginia
                        </span>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          <Smartphone className="w-3.5 h-3.5" />
                          Mobile friendly
                        </span>
                      </div>
                    </div>

                    {/* Section 2: Promotions & Pricing */}
                    <div id="promotions-pricing" className="bg-emerald-50/80 border border-emerald-200 rounded-lg p-6 shadow-sm relative">
                      <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        <Sparkles className="w-3.5 h-3.5" />
                        Limited-time
                      </span>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Promotions & Pricing</h3>
                      <p className="text-sm text-gray-600 mb-4">Two ways to get extended free access.</p>

                      <div className="space-y-4 mb-4">
                        <div className="border border-gray-200 rounded-lg p-4 relative">
                          <span className="absolute top-3 right-3 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Top Deal</span>
                          <div className="flex items-start gap-2 mb-2">
                            <FileText className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-sm text-gray-900">FIRST100 (first 100 businesses)</p>
                              <p className="text-xs text-gray-600">Sign up and use the application free through 12/31/2026.</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <input
                              type="text"
                              readOnly
                              value="FIRST100"
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText('FIRST100').then(() => toast.success('Code copied to clipboard')).catch(() => toast.error('Could not copy'));
                              }}
                              className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                              title="Copy code"
                            >
                              <Copy className="w-4 h-4 text-gray-600" />
                            </button>
                          </div>
                        </div>

                        <div className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start gap-2">
                            <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-sm text-gray-900">Sign up by March 1, 2026</p>
                              <p className="text-xs text-gray-600 mt-0.5">Everyone who signs up before March 1 can use the app through June 30, 2026.</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-gray-200 pt-4 mb-4 relative">
                        <span className="absolute top-4 right-0 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">After promo</span>
                        <div className="flex items-center gap-2 mb-2">
                          <Settings className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <h4 className="font-semibold text-sm text-gray-900">Monthly Pricing</h4>
                        </div>
                        <div className="space-y-2 mt-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-700">First user</span>
                            <span className="font-semibold text-gray-900">$14.99/mo</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-700">Each additional user</span>
                            <span className="font-semibold text-gray-900">$12.99/mo</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">You can add or remove users anytime.</p>
                      </div>

                      <button
                        type="button"
                        onClick={() => setShowContactAACoreModal(true)}
                        className="w-full inline-flex items-center justify-center gap-2 aacore-btn-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Contact Us
                      </button>

                      <div className="flex items-start gap-2 mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <p>Offers may be limited by availability (e.g., FIRST100 is for the first 100 businesses).</p>
                      </div>
                    </div>
                  </>
                )}

                {/* First 100 early adopter offer */}
                <div
                  className="relative overflow-hidden rounded-lg border border-[#dbeafe] shadow-sm"
                  style={{
                    background:
                      'linear-gradient(135deg, #eef6fc 0%, #f8fafc 55%, #f1f5f9 100%)',
                  }}
                >
                  <div className="h-1" style={{ backgroundColor: '#0B4778' }} aria-hidden="true" />
                  <div
                    className="pointer-events-none absolute top-0 right-0 h-44 w-44 rounded-full"
                    style={{
                      background: 'rgba(186, 215, 245, 0.42)',
                      transform: 'translate(42%, -42%)',
                    }}
                    aria-hidden="true"
                  />
                  <div
                    className="pointer-events-none absolute top-0 right-0 h-28 w-28 rounded-full"
                    style={{
                      background: 'rgba(147, 197, 253, 0.28)',
                      transform: 'translate(35%, -35%)',
                    }}
                    aria-hidden="true"
                  />
                  <div className="relative p-5">
                    <div className="mb-3">
                      <span
                        className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                        style={{ backgroundColor: '#eef2ff', color: '#0B4778' }}
                      >
                        Early adopter offer
                      </span>
                    </div>

                    <h3 className="mb-2 text-lg font-bold" style={{ color: '#0B4778', lineHeight: 1.3 }}>
                      3 months free for new business accounts
                    </h3>
                    <p className="mb-4 text-sm text-gray-600">
                      New business accounts start with 3 months free. No credit card is needed to create the account. After the free period, pricing is:
                    </p>

                    <div className="mb-4 rounded-lg border border-gray-200 bg-white/90 p-4">
                      <p className="mb-3 text-sm font-semibold text-gray-900">Monthly pricing</p>
                      <div className="space-y-2.5">
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-sm text-gray-700">First business user, without AI</span>
                          <span className="whitespace-nowrap text-sm font-semibold text-gray-900">$14.99/mo</span>
                        </div>
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-sm text-gray-700">First business user, with AI</span>
                          <span className="whitespace-nowrap text-sm font-semibold text-gray-900">$19.99/mo</span>
                        </div>
                        <div className="flex items-start justify-between gap-3 border-t border-gray-100 pt-2.5">
                          <span className="text-sm text-gray-700">Each additional business user</span>
                          <span className="whitespace-nowrap text-sm font-semibold text-gray-900">$12.99/mo</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {[
                        { title: 'No credit card', text: 'Start setup without payment info' },
                        { title: 'Guided setup', text: 'Use the quick setup guide' },
                      ].map(({ title, text }) => (
                        <div
                          key={title}
                          className="rounded-lg border border-gray-200 bg-white/90 p-3 backdrop-blur-[1px]"
                        >
                          <div className="flex items-start gap-2">
                            <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: '#0B4778' }} aria-hidden="true" />
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{title}</p>
                              <p className="text-xs text-gray-600">{text}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <Link
                        to="/blog/"
                        className="aacore-btn-primary flex-1 justify-center lg:flex-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
                      >
                        <List className="h-4 w-4" aria-hidden="true" />
                        Setup guide
                      </Link>
                      <Link
                        to="/features/"
                        className="aacore-btn-primary flex-1 justify-center lg:flex-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900"
                      >
                        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                        Features page
                      </Link>
                    </div>
                  </div>
                </div>

                {/* What you get with AACore Booking section */}
                <div className="bg-white border border-gray-200 p-5 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">What you get with AACore Booking</h3>
                      <p className="text-sm text-gray-600 mt-1">Everything you need to take bookings and grow.</p>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(11, 71, 120, 0.1)', color: '#0B4778' }}>
                      <Star className="h-3 w-3" />
                      Highlights
                    </span>
                  </div>
                  <div className="space-y-4 mt-4">
                    {/* Feature 1: Low monthly fee */}
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(11, 71, 120, 0.3)', backgroundColor: 'rgba(11, 71, 120, 0.05)' }}>
                        <DollarSign className="h-5 w-5" style={{ color: '#0B4778' }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-900 mb-1">Low monthly fee</p>
                        <p className="text-sm text-gray-600">Predictable pricing designed for small businesses.</p>
                      </div>
                    </div>

                    {/* Feature 2: Beautiful, customizable website */}
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(11, 71, 120, 0.3)', backgroundColor: 'rgba(11, 71, 120, 0.05)' }}>
                        <Palette className="h-5 w-5" style={{ color: '#0B4778' }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-900 mb-1">Beautiful, customizable website</p>
                        <p className="text-sm text-gray-600">A modern booking page that matches your brand and looks great on mobile.</p>
                      </div>
                    </div>

                    {/* Feature 3: $5.99 for months without bookings */}
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(11, 71, 120, 0.3)', backgroundColor: 'rgba(11, 71, 120, 0.05)' }}>
                        <TrendingUp className="h-5 w-5" style={{ color: '#0B4778' }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-900 mb-1">$5.99 for months without bookings</p>
                        <p className="text-sm text-gray-600">If you don't have any bookings in a month, your monthly fee drops automatically.</p>
                      </div>
                    </div>

                    {/* Feature 4: No hidden fees */}
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(11, 71, 120, 0.3)', backgroundColor: 'rgba(11, 71, 120, 0.05)' }}>
                        <FileText className="h-5 w-5" style={{ color: '#0B4778' }} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-900 mb-1">No hidden fees</p>
                        <p className="text-sm text-gray-600">What you see is what you pay. No surprise charges.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Privacy first card */}
                <div className="bg-gray-50 border border-gray-200 p-5 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 border-gray-300 bg-white flex items-center justify-center">
                      <Shield className="h-5 w-5 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm text-gray-900 mb-1">Privacy first</h3>
                      <p className="text-sm text-gray-600">We do not sell your information to any third party. Your data is used only to operate your booking system and support your customers.</p>
                    </div>
                  </div>
                </div>

                {/* Set up in minutes card */}
                <div className="bg-white border border-gray-200 p-5 rounded-lg shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg border-2 bg-white flex items-center justify-center" style={{ borderColor: 'rgba(11, 71, 120, 0.3)' }}>
                      <Zap className="h-5 w-5" style={{ color: '#0B4778' }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm text-gray-900 mb-1">Set up in minutes</h3>
                      <p className="text-sm text-gray-600">Create your business profile now. Next step: create your admin account and you're ready to start accepting bookings.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Verification Modal */}
      <EmailVerificationModal
        isOpen={showVerificationModal}
        onClose={handleCloseVerificationModal}
        onResendVerification={handleResendVerification}
        email={verificationEmail}
        onEmailChange={setVerificationEmail}
        showEmailInput
        isEmailKnownToExist={!isCheckingVerificationEmailExists && isVerificationEmailKnownToExist}
        isLoading={isResendingVerification}
      />

      {/* Contact AA Core Modal */}
      <ContactAACoreModal
        isOpen={showContactAACoreModal}
        onClose={() => setShowContactAACoreModal(false)}
        onSubmit={async (data) => {
          const result = await apiService.submitContactAACore(data);
          if (result.success) {
            toast.success(result.message || 'Your message has been sent. We\'ll be in touch soon.');
          } else {
            throw new Error(result.message);
          }
        }}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BusinessRegistrationPage; 